import {useCallback, useEffect, useRef} from "react";

// iOS Safari does not bring a focused field into view when the page scrolls
// in an inner overflow container: the app shell locks the document with
// overflow-hidden, and Safari's automatic reveal-on-focus only pans the
// document scroller. On the first tap the keyboard and the AutoFill bar
// open right on top of the login field and nothing moves. (Chrome on iOS
// scrolls nested containers itself, so it never shows the bug.)
//
// While a field is focused and the keyboard covers part of the layout
// viewport (the visual viewport shrinks; the layout viewport does not),
// the spacer rendered after the form is sized to the covered height so the
// inner scroller gains scroll range, and the field is scrolled into the
// strip that stays visible above the keyboard.
//
// Ignore shrinks smaller than this: URL-bar show/hide jitter, not a
// keyboard. Also keeps the hook inert on desktop.
const KEYBOARD_MIN_PX = 80;

// How long the spacer is frozen after a blur. A tap on Continue blurs the
// field first, and Safari starts closing the keyboard on that blur, so its
// visual-viewport resize can fire BEFORE the click lands. Resizing the
// spacer then clamps the scroller and slides the button out from under the
// finger, which is the layout-shift-under-tap this hook exists to avoid.
// Comfortably longer than the ~250ms iOS dismiss animation.
const BLUR_SETTLE_MS = 400;

const coveredByKeyboard = () => {
  const vv = window.visualViewport;
  return vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
};

const useKeyboardReveal = () => {
  const fieldRef = useRef(null);
  const spacerRef = useRef(null);
  // Attach to the last element that must stay visible above the keyboard
  // (the Continue button). Falls back to the field itself if unset.
  const actionRef = useRef(null);
  // Set for BLUR_SETTLE_MS after a blur; see BLUR_SETTLE_MS.
  const blurHoldRef = useRef(false);
  const blurTimerRef = useRef(null);

  const sync = useCallback(() => {
    const el = fieldRef.current;
    const spacer = spacerRef.current;
    if (!spacer) return;
    // Frozen: leave the spacer at whatever height the tap saw.
    if (blurHoldRef.current) return;
    // Measured, never inferred from focus: a blurred field with the keyboard
    // still up must keep its spacer, and the keyboard is only gone when the
    // visual viewport says so.
    const kb = coveredByKeyboard();
    // The height is written imperatively, not through state: the write and
    // the scroll math below must land in one synchronous layout pass so the
    // scroller already has its new range when we scroll. A state-driven
    // spacer commits after this handler runs, which is too late; the scroll
    // clamps after a few pixels. (The host must not pass a style prop on
    // the spacer div, or re-renders would wipe this height.)
    spacer.style.height = kb >= KEYBOARD_MIN_PX ? `${kb}px` : "0px";
    if (!el || kb < KEYBOARD_MIN_PX) return;
    // Nearest ancestor that is a real scroll container with room to
    // scroll. Checking only scrollHeight > clientHeight is wrong here: the
    // login card is h-full with visible overflow, so it reports overflow
    // too, and scrollBy on a non-scroll-container is a silent no-op.
    let scroller = el.parentElement;
    while (scroller) {
      const oy = getComputedStyle(scroller).overflowY;
      if (
        (oy === "auto" || oy === "scroll") &&
        scroller.scrollHeight > scroller.clientHeight + 1
      )
        break;
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    // Scroll the MINIMUM that lifts the action area (Continue) above the
    // keyboard: minimal scroll keeps the most content above the field on
    // screen (the Phone/Email toggle). Clamped so the field itself never
    // leaves the top; never scrolls up. Instant scroll on purpose: Safari
    // can cancel smooth scrolls while the keyboard animation runs.
    const vv = window.visualViewport;
    const stripBottom = vv.offsetTop + vv.height;
    const bottomEl = actionRef.current || el;
    const delta = Math.min(
      bottomEl.getBoundingClientRect().bottom + 16 - stripBottom,
      el.getBoundingClientRect().top - vv.offsetTop - 12,
    );
    if (delta > 4) scroller.scrollBy(0, delta);
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    vv.addEventListener("resize", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      clearTimeout(blurTimerRef.current);
    };
  }, [sync]);

  const fieldProps = {
    onFocus: (e) => {
      fieldRef.current = e.target;
      // Focus wins over a pending blur hold: this is a field-to-field move
      // (or a refocus), so the layout should follow the new field now.
      clearTimeout(blurTimerRef.current);
      blurHoldRef.current = false;
      // Keyboard opening fires visual-viewport resizes, which re-sync with
      // final geometry; this direct call covers field-to-field focus where
      // the keyboard is already up and no resize will fire.
      sync();
    },
    onBlur: () => {
      // Drop the field but keep the spacer until the keyboard actually
      // closes. Removing it at blur -- or letting the resize that the blur
      // itself triggers remove it -- shifts the layout under a Continue tap
      // before its click lands, so freeze first and settle after.
      fieldRef.current = null;
      blurHoldRef.current = true;
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = setTimeout(() => {
        blurHoldRef.current = false;
        sync();
      }, BLUR_SETTLE_MS);
    },
  };

  return {fieldProps, spacerRef, actionRef};
};

export default useKeyboardReveal;
